import { CalendarTodayOutlined, MoreVert } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChipLabel, { CHIP_COLORS } from '/@/components/chip-label';
import { useMutateDeleteClaim, useMutateUpdateClaim } from '/@/hooks/claims';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useCapabilities } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { ApplicationRegister } from '/@/types/application-register';
import type { Claim } from '/@/types/claim';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { flowNavigationState, startVisitReportFlow } from '/@/views/visit-reports/flow-tracking';
import ClaimForm from './add/claim';
import { getUserDisplayName, RegistryFooter, RegistryFooterActions } from './card-footer';
import { claimProblemLabel, claimStatusLabel } from './claim-helpers';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { Cell, COLUMNS } from './data-grid';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';
import { TablaCard, TablaCardRow } from './tabla-card';

const RegistryClaim = ({
    claim,
    applications,
    notes,
    cropMonitoring,
    expanded,
    onToggle,
}: {
    claim: Claim;
    /** The claim's related application registers, resolved from the campaign's applications. */
    applications: ApplicationRegister[];
    notes: CropMonitoringNote[];
    cropMonitoring?: CropMonitoring;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const updateClaim = useMutateUpdateClaim();
    const deleteClaim = useMutateDeleteClaim();
    const ownUserNodeQuery = useOwnUserNode();

    const isMobile = Boolean(onToggle);
    const isOpen = claim.status === 'OPEN';
    const isAuthor = ownUserNodeQuery?.data?.id === claim.createdBy?.id;

    const closeMenu = () => setMenuAnchor(null);

    const startEditing = () => {
        closeMenu();
        setEditing(true);
    };

    const startDeleting = () => {
        closeMenu();
        setConfirmingDelete(true);
    };

    const handleClose = async () => {
        try {
            await updateClaim.trigger({ id: claim.id, status: 'CLOSED' });
        } catch {
            enqueueSnackbar(_('No se pudo cerrar el reclamo. Intentá nuevamente.'), { variant: 'error' });
        }
    };

    const problemWithStatus = (
        <Stack direction='row' spacing={isMobile ? 1 : 0.5} alignItems='center'>
            <Typography variant='h6' color='primary'>
                {claimProblemLabel(claim.problem)}
            </Typography>
            <ChipLabel label={claimStatusLabel(claim.status)} type={isOpen ? 'warning' : 'gray'} size='sm' />
        </Stack>
    );

    // The theme sizes buttons to `fit-content`; on mobile the pair splits the row and stretches
    // to the same height even when a label wraps (as in add/application.tsx).
    const mobileActionSx = isMobile ? { flex: 1, height: 'auto' } : undefined;

    const doseLabel = (application: ApplicationRegister) => `${application.dosis} ${application.unity}`.trim();

    // "Crear informe": the report builder for this lote, with the "Reclamos" section and this claim selected
    const { canCreateVisitReports } = useCapabilities();
    const navigate = useNavigate();
    // Goes straight to the editor, so the report's attempt starts here.
    const createReport = () =>
        navigate(
            reverse('visitReport.VisitReportEdit', {
                qs: { fieldIds: String(claim.field), claimId: String(claim.id) },
            }),
            { state: flowNavigationState(startVisitReportFlow('claim')) }
        );

    return (
        <>
            <RegistryCard
                // Mobile titles the card "Reclamo" in its header instead of the floating badge.
                badge={isMobile ? undefined : <ChipLabel label={_('Reclamo')} type='nSmart' />}
                highlightColor={CHIP_COLORS.nSmart.color}
                header={
                    isMobile ? (
                        <Typography variant='h6' color='primary'>
                            {_('Reclamo')}
                        </Typography>
                    ) : (
                        problemWithStatus
                    )
                }
                menu={
                    // Mobile has edit/delete icons in the footer instead — the header only has room
                    // for the expand chevron.
                    isMobile || !isAuthor ? undefined : (
                        <>
                            <IconButton
                                size='small'
                                aria-label={_('Más opciones')}
                                onClick={(event) => setMenuAnchor(event.currentTarget)}
                            >
                                <MoreVert />
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                                <MenuItem onClick={startEditing}>{_('Editar')}</MenuItem>
                                <MenuItem onClick={startDeleting}>{_('Eliminar')}</MenuItem>
                            </Menu>
                        </>
                    )
                }
                footer={
                    <RegistryFooter
                        authorName={getUserDisplayName(claim.createdBy)}
                        date={formatDate(new Date(claim.created))}
                        actions={
                            isMobile && expanded && isAuthor ? (
                                <RegistryFooterActions onEdit={startEditing} onDelete={startDeleting} />
                            ) : undefined
                        }
                    />
                }
                expanded={expanded}
                onToggle={onToggle}
            >
                {isMobile && problemWithStatus}

                <Stack direction='row' spacing={0.5} alignItems='center'>
                    <CalendarTodayOutlined sx={{ fontSize: 12, color: 'primary.onSurface' }} />
                    <Typography variant='caption' fontWeight='bold' color='primary'>
                        {`${_('Fecha de reclamo')}: ${formatDate(dayjs(claim.date).toDate())}`}
                    </Typography>
                </Stack>

                {claim.description && (
                    <Typography variant='body2' color='primary'>
                        {claim.description}
                    </Typography>
                )}

                {applications.length > 0 &&
                    (isMobile ? (
                        <Stack spacing={1} sx={{ width: '100%' }}>
                            <Typography variant='body2' fontWeight='bold' color='primary'>
                                {_('Aplicaciones relacionadas')}
                            </Typography>
                            {applications.map((application) => (
                                <TablaCard key={application.id} variant='filled'>
                                    <TablaCardRow
                                        label={_('Fecha de aplicación')}
                                        value={formatDate(dayjs(application.applicationDate).toDate())}
                                    />
                                    <TablaCardRow label={_('Producto')} value={application.cropProtectionProductName} />
                                    <TablaCardRow label={_('Dosis')} value={doseLabel(application)} />
                                </TablaCard>
                            ))}
                        </Stack>
                    ) : (
                        <Stack spacing={1} sx={{ bgcolor: 'grey.100', p: 1, width: '100%' }}>
                            <Typography variant='body2' fontWeight='bold' color='primary'>
                                {_('Aplicaciones relacionadas')}
                            </Typography>
                            <Box
                                sx={{ display: 'grid', gridTemplateColumns: COLUMNS, width: '100%', overflow: 'auto' }}
                            >
                                <Cell header>{_('Fecha de aplicación')}</Cell>
                                <Cell header>{_('Producto')}</Cell>
                                <Cell header>{_('Dosis')}</Cell>
                                {applications.map((application) => (
                                    <Fragment key={application.id}>
                                        <Cell>{formatDate(dayjs(application.applicationDate).toDate())}</Cell>
                                        <Cell>{application.cropProtectionProductName}</Cell>
                                        <Cell>{doseLabel(application)}</Cell>
                                    </Fragment>
                                ))}
                            </Box>
                        </Stack>
                    ))}

                <RegistryNotes notes={notes} fieldId={claim.field} claimId={claim.id} />

                <Stack direction='row' justifyContent='flex-end' spacing={1} sx={{ width: '100%' }}>
                    {isOpen && (
                        <Button
                            variant='outlined'
                            color='primary'
                            sx={mobileActionSx}
                            onClick={handleClose}
                            disabled={updateClaim.isMutating}
                        >
                            {_('Marcar como cerrado')}
                        </Button>
                    )}
                    {canCreateVisitReports && (
                        <Button variant='contained' color='primary' sx={mobileActionSx} onClick={createReport}>
                            {_('Crear informe')}
                        </Button>
                    )}
                </Stack>
            </RegistryCard>

            {editing && (
                <ClaimForm
                    open
                    onClose={() => setEditing(false)}
                    onSuccess={() => setEditing(false)}
                    fieldId={claim.field}
                    cropMonitoring={cropMonitoring}
                    claim={claim}
                />
            )}

            <ConfirmDeleteModal
                open={confirmingDelete}
                onClose={() => setConfirmingDelete(false)}
                onConfirm={() => deleteClaim.trigger({ id: claim.id })}
                title={_('¿Eliminar el reclamo?')}
                message={_('Se van a eliminar el reclamo y sus notas. Esta acción no se puede deshacer.')}
                isDeleting={deleteClaim.isMutating}
            />
        </>
    );
};

export default RegistryClaim;
